// assets/js/builder.js
// ✅ Fix principaux :
// - CSRF fiable: data-csrf + meta fallback
// - On envoie _csrf dans le JSON + X-CSRF-Token header
// - On vérifie que la réponse est JSON + { ok: true } (sinon => pas “Enregistré ✅”)
// - Normalisation video.url => embed url avant save
// - Pas de re-render global à chaque frappe (focus stable)
// - Détection explicite des réponses HTML / redirections (CSRF ou mauvais handler)

function uid() {
    return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function getCsrf(root) {
    const fromData = root?.getAttribute("data-csrf") || "";
    if (fromData) return fromData;

    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? (meta.getAttribute("content") || "") : "";
}

function blockDefault(type) {
    if (type === "heading") return {id: uid(), type, props: {level: 2, text: "Titre"}};
    if (type === "text") return {id: uid(), type, props: {text: "Ton texte…"}};
    if (type === "image") return {id: uid(), type, props: {image_id: 0, alt: ""}};
    if (type === "video") return {id: uid(), type, props: {url: ""}};
    if (type === "divider") return {id: uid(), type, props: {}};
    return null;
}

/* ------------------------------------------------------------------
   VIDEO HELPERS
------------------------------------------------------------------- */

function normalizeVideoInput(value) {
    return (value || "").toString().trim();
}

function parseYouTube(value) {
    const v = normalizeVideoInput(value);
    if (!v) return null;

    // ID direct
    if (/^[a-zA-Z0-9_-]{6,}$/.test(v) && !v.includes("/") && !v.includes(".")) {
        const id = v;
        return {provider: "youtube", id, embedUrl: `https://www.youtube-nocookie.com/embed/${id}`};
    }

    // watch?v=ID
    let m = v.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "youtube", id, embedUrl: `https://www.youtube-nocookie.com/embed/${id}`};
    }

    // youtu.be/ID
    m = v.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "youtube", id, embedUrl: `https://www.youtube-nocookie.com/embed/${id}`};
    }

    // youtube.com/shorts/ID
    m = v.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "youtube", id, embedUrl: `https://www.youtube-nocookie.com/embed/${id}`};
    }

    // youtube.com/embed/ID or youtube-nocookie.com/embed/ID
    m = v.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "youtube", id, embedUrl: `https://www.youtube-nocookie.com/embed/${id}`};
    }

    return null;
}

function parseVimeo(value) {
    const v = normalizeVideoInput(value);
    if (!v) return null;

    // ID direct
    if (/^\d{6,}$/.test(v) && !v.includes("/") && !v.includes(".")) {
        const id = v;
        return {provider: "vimeo", id, embedUrl: `https://player.vimeo.com/video/${id}`};
    }

    // vimeo.com/ID
    let m = v.match(/vimeo\.com\/(\d{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "vimeo", id, embedUrl: `https://player.vimeo.com/video/${id}`};
    }

    // player.vimeo.com/video/ID
    m = v.match(/player\.vimeo\.com\/video\/(\d{6,})/);
    if (m?.[1]) {
        const id = m[1];
        return {provider: "vimeo", id, embedUrl: `https://player.vimeo.com/video/${id}`};
    }

    return null;
}

function parseVideo(value) {
    return parseYouTube(value) || parseVimeo(value);
}

function safeText(s) {
    return (s || "").toString();
}

/** ✅ Normalise les blocks avant envoi : convertit video.url -> embed url (si possible) */
function normalizeBlocksForSave(blocks) {
    return (blocks || []).map((b) => {
        if (!b || typeof b !== "object") return b;
        if (b.type !== "video") return b;

        const props = b.props && typeof b.props === "object" ? b.props : {};
        const input = normalizeVideoInput(props.url || "");

        if (!input) return {...b, props: {url: ""}};

        const parsed = parseVideo(input);
        if (!parsed) return {...b, props: {url: input}};

        return {...b, props: {url: parsed.embedUrl}};
    });
}

export function initBuilder() {
    const root = document.querySelector(".builder__layout");
    if (!root) return;

    const blocksEl = document.getElementById("builderBlocks");
    const propsEl = document.getElementById("builderProps");
    const propsEmptyEl = document.getElementById("builderPropsEmpty");
    const delBtn = document.getElementById("builderDelete");
    const saveBtn = document.getElementById("builderSave");

    if (!blocksEl || !propsEl || !propsEmptyEl || !delBtn || !saveBtn) return;

    const articleId = root.getAttribute("data-article-id");
    const initial = root.getAttribute("data-initial") || "[]";

    let blocks = [];
    try {
        const parsed = JSON.parse(initial);
        blocks = Array.isArray(parsed) ? parsed : [];
    } catch {
        blocks = [];
    }

    // ✅ migration douce : anciens blocs video (provider/video_id) -> url embed
    blocks = blocks.map((b) => {
        if (b?.type !== "video") return b;
        const p = b.props || {};

        if (typeof p.url === "string") return b;

        if (p.provider && p.video_id) {
            const provider = String(p.provider);
            const id = String(p.video_id);
            let url = "";
            if (provider === "youtube") url = `https://www.youtube-nocookie.com/embed/${id}`;
            if (provider === "vimeo") url = `https://player.vimeo.com/video/${id}`;
            return {...b, props: {url}};
        }

        return {...b, props: {url: ""}};
    });

    let selectedId = null;

    function select(id) {
        selectedId = id;
        render();
    }

    function renderBlocksList() {
        blocksEl.innerHTML = "";

        blocks.forEach((b, index) => {
            const item = document.createElement("div");
            item.className = "builder-block" + (b.id === selectedId ? " is-selected" : "");
            item.setAttribute("data-id", b.id);

            const title = document.createElement("div");
            title.className = "builder-block__title";
            title.textContent = `${index + 1}. ${b.type}`;
            item.appendChild(title);

            const preview = document.createElement("div");
            preview.className = "builder-block__preview";

            if (b.type === "heading") preview.textContent = safeText(b.props?.text || "");
            if (b.type === "text") preview.textContent = safeText(b.props?.text || "").slice(0, 120);
            if (b.type === "image") preview.textContent = `image_id: ${b.props?.image_id || 0}`;
            if (b.type === "video") {
                const url = safeText(b.props?.url || "");
                const parsed = parseVideo(url);
                preview.textContent = parsed ? `${parsed.provider}: ${parsed.id}` : (url ? `URL invalide: ${url}` : "URL vidéo vide");
            }
            if (b.type === "divider") preview.textContent = "—";

            item.appendChild(preview);

            const actions = document.createElement("div");
            actions.className = "builder-block__actions";

            const up = document.createElement("button");
            up.type = "button";
            up.textContent = "↑";
            up.onclick = (e) => {
                e.stopPropagation();
                if (index <= 0) return;
                const tmp = blocks[index - 1];
                blocks[index - 1] = blocks[index];
                blocks[index] = tmp;
                render();
            };

            const down = document.createElement("button");
            down.type = "button";
            down.textContent = "↓";
            down.onclick = (e) => {
                e.stopPropagation();
                if (index >= blocks.length - 1) return;
                const tmp = blocks[index + 1];
                blocks[index + 1] = blocks[index];
                blocks[index] = tmp;
                render();
            };

            actions.appendChild(up);
            actions.appendChild(down);
            item.appendChild(actions);

            item.onclick = () => select(b.id);
            blocksEl.appendChild(item);
        });
    }

    function renderProps() {
        const selected = blocks.find((b) => b.id === selectedId);

        propsEl.innerHTML = "";
        delBtn.hidden = !selected;

        if (!selected) {
            propsEmptyEl.style.display = "";
            return;
        }

        propsEmptyEl.style.display = "none";

        function field(label, input) {
            const wrap = document.createElement("div");
            wrap.className = "builder-field";
            const l = document.createElement("label");
            l.textContent = label;
            wrap.appendChild(l);
            wrap.appendChild(input);
            return wrap;
        }

        function refreshSelectedPreviewOnly() {
            const el = blocksEl.querySelector(`.builder-block[data-id="${selectedId}"] .builder-block__preview`);
            if (!el) return;
            const b = blocks.find((x) => x.id === selectedId);
            if (!b) return;

            if (b.type === "heading") el.textContent = safeText(b.props?.text || "");
            if (b.type === "text") el.textContent = safeText(b.props?.text || "").slice(0, 120);
            if (b.type === "image") el.textContent = `image_id: ${b.props?.image_id || 0}`;
            if (b.type === "video") {
                const url = safeText(b.props?.url || "");
                const parsed = parseVideo(url);
                el.textContent = parsed ? `${parsed.provider}: ${parsed.id}` : (url ? `URL invalide: ${url}` : "URL vidéo vide");
            }
            if (b.type === "divider") el.textContent = "—";
        }

        if (!selected.props || typeof selected.props !== "object") selected.props = {};

        if (selected.type === "heading") {
            const level = document.createElement("select");
            [1, 2, 3].forEach((n) => {
                const o = document.createElement("option");
                o.value = String(n);
                o.textContent = `H${n}`;
                if ((selected.props?.level ?? 2) === n) o.selected = true;
                level.appendChild(o);
            });

            level.onchange = () => {
                selected.props.level = parseInt(level.value, 10);
                refreshSelectedPreviewOnly();
            };

            const text = document.createElement("input");
            text.type = "text";
            text.value = selected.props?.text || "";
            text.oninput = () => {
                selected.props.text = text.value;
                refreshSelectedPreviewOnly();
            };

            propsEl.appendChild(field("Niveau", level));
            propsEl.appendChild(field("Texte", text));
        }

        if (selected.type === "text") {
            const ta = document.createElement("textarea");
            ta.rows = 10;
            ta.value = selected.props?.text || "";
            ta.oninput = () => {
                selected.props.text = ta.value;
                refreshSelectedPreviewOnly();
            };
            propsEl.appendChild(field("Texte", ta));
        }

        if (selected.type === "image") {
            const id = document.createElement("input");
            id.type = "number";
            id.value = selected.props?.image_id || 0;
            id.oninput = () => {
                selected.props.image_id = parseInt(id.value, 10) || 0;
                refreshSelectedPreviewOnly();
            };

            const alt = document.createElement("input");
            alt.type = "text";
            alt.value = selected.props?.alt || "";
            alt.oninput = () => {
                selected.props.alt = alt.value;
            };

            propsEl.appendChild(field("Image ID", id));
            propsEl.appendChild(field("Alt", alt));
        }

        if (selected.type === "video") {
            const input = document.createElement("input");
            input.type = "text";
            input.placeholder = "Colle un lien YouTube/Vimeo OU un ID…";
            input.value = selected.props?.url || "";

            const hint = document.createElement("div");
            hint.className = "builder-hint";

            const setHint = (txt) => (hint.textContent = txt);

            function updateHintFrom(val) {
                const v = normalizeVideoInput(val);
                if (!v) {
                    setHint("Ex: https://youtu.be/ID • https://www.youtube.com/watch?v=ID • https://vimeo.com/123456");
                    return;
                }

                const parsed = parseVideo(v);
                if (!parsed) {
                    setHint("URL/ID non reconnu. Formats: YouTube (watch, youtu.be, shorts) / Vimeo (vimeo.com/ID).");
                    return;
                }

                setHint(`✅ Détecté: ${parsed.provider} — ID: ${parsed.id}`);
            }

            input.oninput = () => {
                selected.props.url = input.value;
                updateHintFrom(input.value);
                refreshSelectedPreviewOnly();
            };

            updateHintFrom(input.value);

            propsEl.appendChild(field("URL vidéo (YouTube/Vimeo)", input));
            propsEl.appendChild(hint);
        }

        delBtn.onclick = () => {
            blocks = blocks.filter((b) => b.id !== selectedId);
            selectedId = null;
            render();
        };
    }

    function render() {
        renderBlocksList();
        renderProps();
    }

    document.querySelectorAll(".builderAdd").forEach((btn) => {
        btn.addEventListener("click", () => {
            const type = btn.getAttribute("data-type");
            const b = blockDefault(type);
            if (!b) return;
            blocks.push(b);
            select(b.id);
        });
    });

    // ✅ SAVE
    saveBtn.onclick = async () => {
        const payloadBlocks = normalizeBlocksForSave(blocks);
        const csrf = getCsrf(root);

        const res = await fetch(`/article/edit/${articleId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": csrf,
                "X-Requested-With": "fetch",
            },
            body: JSON.stringify({
                _csrf: csrf,
                blocks: payloadBlocks,
            }),
            credentials: "same-origin",
            redirect: "follow",
        });

        const ct = (res.headers.get("content-type") || "").toLowerCase();

        // Si redirection ou HTML => CSRF/handler wrong
        if (res.redirected || ct.includes("text/html")) {
            const txt = await res.text().catch(() => "");
            console.error("Réponse HTML (redirect/CSRF/branche FORM):", res.status, res.url, txt.slice(0, 1200));
            alert("Sauvegarde refusée (CSRF ou redirection). Ouvre la console (F12) pour voir la cause.");
            return;
        }

        if (!ct.includes("application/json")) {
            const txt = await res.text().catch(() => "");
            console.error("Réponse non JSON:", res.status, txt.slice(0, 1200));
            alert("Erreur sauvegarde: le serveur ne renvoie pas du JSON.");
            return;
        }

        const data = await res.json().catch(() => null);

        if (!res.ok || !data || data.ok !== true) {
            console.error("Save error:", res.status, data);
            alert(data?.error || "Erreur sauvegarde");
            return;
        }

        blocks = payloadBlocks;
        alert("Enregistré ✅");
        render();
    };

    render();
}
